import { convexQuery } from "@convex-dev/react-query";
import type { Id } from "@convex/_generated/dataModel";
import { PERMISSION_KEYS } from "@convex/permissions";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { PlusCircle, Users } from "lucide-react";
import { useEffect, useState } from "react";

import {
  organizationRolesCreateRole,
  organizationRolesDeleteRole,
  organizationRolesGetRoleById,
  organizationRolesGetRoleEmployees,
  organizationRolesGetRoles,
} from "~/api-refs";
import {
  RoleForm,
  buildDefaultPermissions,
} from "~/components/employees/role-form";
import { LoadingPanel } from "~/components/shared/loading-panel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Spinner } from "~/components/ui/spinner";
import { convexClient } from "~/integrations/convex/provider";
import { ProductEvents } from "~/lib/posthog";

export const Route = createFileRoute("/_authenticated/employees/manage-roles")({
  component: ManageRolesModal,
});

function ManageRolesModal() {
  const navigate = useNavigate();
  const [roleToDelete, setRoleToDelete] = useState<{
    name: string;
    id: Id<"organizationRoles">;
  } | null>(null);

  const handleClose = () => {
    void navigate({ to: "/employees" });
  };

  const deleteRoleMutation = useMutation({
    mutationFn: ({ roleId }: { roleId: Id<"organizationRoles"> }) =>
      convexClient.mutation(organizationRolesDeleteRole, { roleId }),
  });

  return (
    <>
      <Dialog
        onOpenChange={(open: boolean) => {
          if (!open) {
            handleClose();
          }
        }}
        open={roleToDelete === null}
      >
        <DialogContent
          className="max-h-[85vh] w-full max-w-4xl overflow-y-auto sm:max-w-4xl"
          noScrollPrevention
        >
          <DialogHeader>
            <DialogTitle>Manage Roles</DialogTitle>
            <DialogDescription>
              Create and manage preset permissions for easier employee
              management.
            </DialogDescription>
          </DialogHeader>
          <ManageRolesModalContent setRoleToDelete={setRoleToDelete} />
        </DialogContent>
      </Dialog>
      <AlertDialog
        onOpenChange={(open: boolean) => {
          if (!open) {
            setRoleToDelete(null);
          }
        }}
        open={roleToDelete !== null}
      >
        {roleToDelete && (
          <DeleteDialogContent
            onDelete={async () => {
              await deleteRoleMutation.mutateAsync({ roleId: roleToDelete.id });
              ProductEvents.roleDeleted(roleToDelete.id);
              setRoleToDelete(null);
            }}
            roleToDelete={roleToDelete}
          />
        )}
      </AlertDialog>
    </>
  );
}

const DeleteDialogContent = ({
  onDelete,
  roleToDelete,
}: {
  onDelete: () => void;
  roleToDelete: { name: string; id: Id<"organizationRoles"> };
}) => {
  const { data: employees } = useQuery(
    convexQuery(organizationRolesGetRoleEmployees, {
      roleId: roleToDelete.id,
    })
  );

  if (!employees) {
    return (
      <AlertDialogContent>
        <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
          <Spinner className="size-6" />
          <p className="body-sm text-muted-foreground">
            Loading assigned employees...
          </p>
        </div>
      </AlertDialogContent>
    );
  }

  return (
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete role</AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to delete <b>{roleToDelete.name}</b>? This role
          will be removed from all employees.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <div className="space-y-3">
        <p className="label text-foreground">
          {employees.length > 0
            ? `Employees to be unassigned (${employees.length.toString()})`
            : "No employees have this role."}
        </p>
        {employees.map((employeeRole) => (
          <div className="flex items-center gap-3" key={employeeRole._id}>
            <img
              alt={`${employeeRole.employee?.firstName} ${employeeRole.employee?.lastName}'s profile`}
              className="rounded-full"
              height={32}
              src={
                employeeRole.employee?.imageUrl ??
                "/images/avatar-placeholder.svg"
              }
              width={32}
            />
            <p className="body-sm text-foreground">
              {employeeRole.employee?.firstName}{" "}
              {employeeRole.employee?.lastName}
            </p>
          </div>
        ))}
      </div>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onDelete} variant="destructive">
          Delete Role
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  );
};

const ManageRolesModalContent = ({
  setRoleToDelete,
}: {
  setRoleToDelete: (
    role: { name: string; id: Id<"organizationRoles"> } | null
  ) => void;
}) => {
  const [selectedRoleId, setSelectedRoleId] = useState<
    Id<"organizationRoles"> | undefined
  >(undefined);

  const { data: roles } = useQuery(convexQuery(organizationRolesGetRoles, {}));

  const createRoleMutation = useMutation({
    mutationFn: (params: {
      name: string;
      type: "custom";
      permissions: Record<(typeof PERMISSION_KEYS)[number], boolean>;
    }) => convexClient.mutation(organizationRolesCreateRole, params),
  });

  const handleCreateRole = async () => {
    const newRoleId = await createRoleMutation.mutateAsync({
      name: "New Role",
      type: "custom",
      permissions: buildDefaultPermissions(null),
    });
    ProductEvents.roleCreated(newRoleId);
    setSelectedRoleId(newRoleId);
  };

  useEffect(() => {
    if (roles && !selectedRoleId) {
      setSelectedRoleId(roles[0]?._id);
    }
  }, [roles, selectedRoleId]);

  if (!roles) {
    return <LoadingPanel message="Loading roles..." />;
  }

  return (
    <div className="grid min-h-[400px] grid-cols-1 gap-4 lg:grid-cols-[14rem_1fr] lg:gap-8">
      <div className="border-border flex flex-col justify-between lg:border-r lg:pr-6">
        <div aria-label="Select a role to manage" role="tablist">
          <div className="flex flex-col gap-1">
            {roles.map((role) => (
              <button
                aria-selected={selectedRoleId === role._id}
                className={`text-foreground block w-full min-w-0 gap-2 truncate rounded-lg px-3 py-2 text-left text-sm font-medium transition-[color,box-shadow] ${selectedRoleId === role._id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                key={role._id}
                onClick={() => setSelectedRoleId(role._id)}
                role="tab"
                type="button"
              >
                {role.name}
              </button>
            ))}
          </div>
        </div>
        <button
          className="text-foreground hover:bg-muted disabled:bg-muted disabled:text-muted-foreground mt-4 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:border-none"
          onClick={handleCreateRole}
          type="button"
        >
          <PlusCircle className="size-4" />
          Create Role
        </button>
      </div>
      <div className="min-w-0">
        {roles.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center">
            <Users className="text-muted-foreground size-10" />
            <p className="label text-foreground mt-2">
              Create a role to get started!
            </p>
            <Button className="mt-4" onClick={handleCreateRole} type="button">
              Create New Role
            </Button>
          </div>
        )}
        {selectedRoleId && (
          <RoleSettings
            roleId={selectedRoleId}
            setRoleToDelete={setRoleToDelete}
          />
        )}
      </div>
    </div>
  );
};

const RoleSettings = ({
  roleId,
  setRoleToDelete,
}: {
  roleId: Id<"organizationRoles">;
  setRoleToDelete: (
    role: { name: string; id: Id<"organizationRoles"> } | null
  ) => void;
}) => {
  const { data: role } = useQuery(
    convexQuery(organizationRolesGetRoleById, { roleId })
  );

  if (!role) {
    return <LoadingPanel message="Loading role settings..." />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-foreground text-base font-semibold text-balance">
          Edit Role: {role.name}
        </h3>
        <p className="text-muted-foreground text-sm">
          Customize permissions for this role
        </p>
      </div>
      <RoleForm role={role} />
      <div className="border-border border-t pt-4">
        <p className="text-destructive text-sm font-medium">Danger zone</p>
        <p className="text-muted-foreground mt-1 text-sm">
          Delete this role and remove it from all assigned employees.
        </p>
        <Button
          className="mt-3"
          onClick={async () => {
            setRoleToDelete({ name: role.name, id: role._id });
          }}
          size="sm"
          type="button"
          variant="destructive"
        >
          Delete Role
        </Button>
      </div>
    </div>
  );
};
